import { createMemo, createResource, onCleanup, type JSX, Show } from "solid-js"
import { createStore } from "solid-js/store"
import { useLocation } from "@solidjs/router"
import { createSimpleContext } from "@opencode-ai/ui/context"
import { useLanguage } from "@/context/language"
import { datoxAgentMode, getCurrentUser, listCompanies, setDatoxAuthTokenProvider } from "@/datox/api"
import { LeshaBootSkeleton } from "@/datox/v2/reports-skeleton"
import { applyLeshaHead } from "@/datox/v2/brand-head"
import { LeshaSignIn } from "@/datox/v2/sign-in"
import { activeCompanyID, setActiveCompanyID } from "@/datox/local-auth"
export { activeCompanyID, localDevHeaders, setActiveCompanyID } from "@/datox/local-auth"

type DatoxAuthState = {
  initialized: boolean
  accountName: string
  accountEmail: string
  accessToken: string
  error: string
}

const defaultTenantID = "95c228f4-944c-49ba-abcd-a00985e5497c"
const defaultScope = "api://d76b434a-b9ac-4bb1-b417-40d2eb65d624/user_impersonation"

export const { provider: DatoxAuthProvider, use: useDatoxAuth } = createSimpleContext({
  name: "DatoxAuth",
  gate: false,
  init: () => {
    const [state, setState] = createStore<DatoxAuthState>({
      initialized: false,
      accountName: "",
      accountEmail: "",
      accessToken: "",
      error: "",
    })

    const [client] = createResource(async () => {
      if (!isMsalConfigured()) {
        setState("initialized", true)
        setState("error", msalRequired() ? "datox.login.msalMissing" : "")
        return null
      }
      try {
        const msal = await import("@azure/msal-browser")
        const instance = new msal.PublicClientApplication({
          auth: {
            clientId: import.meta.env.VITE_DATOX_MSAL_CLIENT_ID || "",
            authority: `https://login.microsoftonline.com/${import.meta.env.VITE_DATOX_MSAL_TENANT_ID || defaultTenantID}`,
            redirectUri: import.meta.env.VITE_DATOX_MSAL_REDIRECT_URI || window.location.origin,
          },
          cache: {
            // Report previews open in independent tabs. MSAL's durable cache
            // must be shared; redirect/interaction state remains tab-local.
            cacheLocation: "localStorage",
            storeAuthStateInCookie: false,
          },
        })
        await instance.initialize()
        const redirect = await instance.handleRedirectPromise()
        if (redirect?.account) instance.setActiveAccount(redirect.account)
        if (!instance.getActiveAccount() && instance.getAllAccounts()[0]) instance.setActiveAccount(instance.getAllAccounts()[0])
        setState("initialized", true)
        await acquireToken(instance)
        return instance
      } catch (error) {
        setState("initialized", true)
        setState("error", error instanceof Error ? error.message : String(error))
        return null
      }
    })

    const login = async () => {
      const instance = client()
      if (!instance) return
      await instance.loginRedirect({ scopes: [scope()], redirectUri: redirectUri(), state: window.location.href })
    }

    const logout = async () => {
      const instance = client()
      if (!instance) return
      await instance.logoutRedirect()
    }

    const token = async (forceRefresh = false) => {
      const instance = client()
      if (!instance) return ""
      return acquireToken(instance, forceRefresh)
    }

    const configured = createMemo(isMsalConfigured)
    const authenticated = createMemo(() => !msalRequired() || !!state.accessToken)

    return {
      state,
      configured,
      authenticated,
      initializing: () => client.loading,
      login,
      logout,
      token,
    }

    async function acquireToken(instance: import("@azure/msal-browser").PublicClientApplication, forceRefresh = false) {
      const account = instance.getActiveAccount() || instance.getAllAccounts()[0]
      if (!account) {
        setState({ initialized: true, accountName: "", accountEmail: "", accessToken: "" })
        return ""
      }
      try {
        const response = await instance.acquireTokenSilent({ scopes: [scope()], account, forceRefresh })
        setState({
          initialized: true,
          accountName: response.account?.name || account.name || "",
          accountEmail: response.account?.username || account.username || "",
          accessToken: response.accessToken,
          error: "",
        })
        return response.accessToken
      } catch (error) {
        setState({ accessToken: "", error: error instanceof Error ? error.message : String(error) })
        return ""
      }
    }
  },
})

export function DatoxAuthGate(props: { children: JSX.Element }) {
  const auth = useDatoxAuth()
  const language = useLanguage()
  const location = useLocation()
  setDatoxAuthTokenProvider(auth.token)
  const scoped = () => datoxAgentMode() && msalRequired() && !location.pathname.startsWith("/review/")
  const [companyAccess, companyActions] = createResource(
    () => (scoped() && auth.authenticated() ? auth.state.accountEmail || true : undefined),
    () => listCompanies().then(async (available) => {
      if (available.length) {
        if (!available.some((company) => company.id === activeCompanyID())) setActiveCompanyID(available[0].id)
        return "ready"
      }
      return getCurrentUser().then((user) => user.is_datox_admin ? "ready" : "denied").catch(() => "denied")
    }).catch(() => "error"),
  )
  const syncCompany = (event: StorageEvent) => {
    if (event.key === "activeCompanyId" && event.oldValue !== event.newValue) window.location.assign("/")
  }
  window.addEventListener("storage", syncCompany)
  onCleanup(() => window.removeEventListener("storage", syncCompany))

  if (!datoxAgentMode() || location.pathname.startsWith("/review/")) return props.children

  applyLeshaHead(document, language.t("datox.v2.title.app"))
  const loading = () => <LeshaBootSkeleton label={language.t("datox.login.loading")} title={language.t("datox.v2.title.app")} />

  return (
    <Show when={!auth.initializing()} fallback={loading()}>
      <Show when={auth.authenticated()} fallback={<DatoxLoginPage />}>
        <Show when={!scoped() || !companyAccess.loading} fallback={loading()}>
          <Show when={!scoped() || companyAccess() === "ready"} fallback={
            <div class="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-v2-text-text-base">
              <p role="alert">{language.t(companyAccess() === "error" ? "datox.admin.loadFailed" : "datox.company.noAccess")}</p>
              <button type="button" onClick={() => void companyActions.refetch()}>{language.t("datox.members.retry")}</button>
              <button type="button" onClick={() => void auth.logout()}>{language.t("datox.profile.logout")}</button>
            </div>
          }>
            {props.children}
          </Show>
        </Show>
      </Show>
    </Show>
  )

  function DatoxLoginPage() {
    return <LeshaSignIn
      logoAlt={language.t("lesha.logo.alt")}
      title={language.t("datox.v2.login.title")}
      subtitle={language.t("datox.v2.login.description")}
      button={auth.initializing() ? language.t("datox.login.loading") : language.t("datox.login.button")}
      footer={language.t("datox.login.footer")}
      error={auth.state.error === "datox.login.msalMissing" ? language.t("datox.login.msalMissing") : auth.state.error || undefined}
      busy={auth.initializing()}
      disabled={auth.initializing() || !auth.configured()}
      onLogin={() => void auth.login()}
    />
  }
}

function isMsalConfigured() {
  return !!import.meta.env.VITE_DATOX_MSAL_CLIENT_ID
}

function redirectUri() {
  return import.meta.env.VITE_DATOX_MSAL_REDIRECT_URI || window.location.origin
}

function msalRequired() {
  if (!datoxAgentMode()) return false
  if (isMsalConfigured()) return true
  try {
    const url = new URL(import.meta.env.VITE_DATOX_AGENT_API_URL || "http://localhost:8088")
    return url.hostname !== "localhost" && url.hostname !== "127.0.0.1"
  } catch {
    return true
  }
}

function scope() {
  return import.meta.env.VITE_DATOX_MSAL_SCOPE || defaultScope
}
